import { apiClient } from './index'
import { READ_DENIED, allowedRead, deniedRead, httpStatusOf } from './policy'
import { checkAuthActionAccess } from '@/utils/checkAuthActionAccess'

/**
 * **권한 밖일 수 있는 조회 한 곳** — 「부를 수 있는가」와 「못 읽었다」를 같은 자리에서 답한다.
 *
 * 이 앱의 403 은 기본적으로 세션을 끝낸다(`plugins/http/index.js`). 그래서 **내가 볼 수 없는 것이 섞인
 * 조회**는 화면마다 두 가지를 따로 적어 왔다: 셸 조회는 액션 게이트(`sa_actions` 에 없으면 아예 안 부른다),
 * 부가 조회는 요청 옵트아웃(`logoutOn403:false`). 같은 화면 안에서도 어떤 읽기는 게이트만, 어떤 읽기는
 * 옵트아웃만 갖게 되면 **빠진 쪽의 클릭 한 번이 로그아웃**이 된다 — 계정 화면이 정확히 그랬다(쓰기에는
 * 옵트아웃이 있고 다이얼로그를 여는 읽기에는 없었다).
 *
 * 그래서 두 축을 한 함수가 함께 가진다:
 *
 * 1. **게이트** — `action` 이 세션에 없으면 요청을 **보내지 않는다**. 보안 계층의 403 은 서버가 그 세션을
 *    끊으므로 클라이언트 옵트아웃으로 되살릴 수 없다([READ_DENIED.ACTION]).
 * 2. **옵트아웃** — 보내는 요청에는 언제나 `logoutOn403:false` 를 싣는다. 업무 계층의 403 은 「이 세션이
 *    무효다」가 아니라 「그것은 네가 볼 수 없다」는 **답**이다([READ_DENIED.FORBIDDEN]).
 *
 * 어느 갈래든 결과는 값이다([allowedRead]·[deniedRead]) — 호출부는 상태 코드를 다시 세지 않고 `allowed`
 * 하나만 본다. 403 이 아닌 실패(500·전송 오류)는 그대로 던진다: 그건 「권한 밖」이 아니라 진짜 실패이고
 * 인터셉터가 이미 토스트로 말한다.
 *
 * 토스트는 **기본적으로** 끄지 않는다 — 서버 문장을 사람이 보는 것이 대체 동작의 절반이다. 다만 403 이
 * 아닌 실패가 그 화면에서 **오류가 아닌** 조회가 있다(§16.175): 콜팝 변경 이력은 「전체를 볼 수 있는
 * 계정인가」를 한 번 물어보는데, 그 400 은 답이지 사고가 아니다. 그런 조회만 `showErrorMessage: false`
 * 를 spec 에 실어 넘긴다 — 규칙은 `logoutOn403` 과 같다(대체 동작을 가진 호출부만).
 *
 * 스스로 다시 부르는 조회(폴링)는 `retry: false` 를 실어 넘긴다 — 재시도와 폴링이 겹치면 요청이 세 배가 된다(`policy.retryDelay`).
 *
 * @param {{action?: string, url: string, params?: object, showLoading?: boolean, showErrorMessage?: boolean,
 *   retry?: boolean}} spec
 * @returns {Promise<{allowed: boolean, data: *, reason: ?string, message: string}>}
 */
export async function permittedRead(spec) {
  const { action, url, params, showLoading, showErrorMessage, retry } = spec || {}

  if (!canRead(spec)) return deniedRead(READ_DENIED.ACTION)

  const config = Object.assign(
    { logoutOn403: false },
    params ? { params } : null,
    showLoading === false ? { showLoading: false } : null,
    showErrorMessage === false ? { showErrorMessage: false } : null,
    retry === false ? { retry: false } : null
  )

  try {
    const response = await apiClient.get(url, config)

    return allowedRead(response.data)
  } catch (error) {
    if (httpStatusOf(error) === 403) return deniedRead(READ_DENIED.FORBIDDEN, error && error.message)

    throw error
  }
}

/**
 * 이 조회를 **부를 수 있는가** — [permittedRead] 가 쓰는 것과 같은 게이트다.
 *
 * 화면이 미리 알아야 할 때가 있다(후보를 못 읽는 칸은 열려 있으면 안 된다). 그 판정을 화면이 따로 적으면
 * 게이트가 둘이 되므로, 요청 spec 을 그대로 주고 여기서 답한다.
 */
export function canRead(spec) {
  const action = spec && spec.action

  return !action || checkAuthActionAccess(action)
}
